/**
 * src/grc/grcFrameworks.js
 *
 * GRC-portal subset of the main framework catalogue.
 * Maps a framework id to its display label and the control list
 * drawn from the shared framework constants.
 */

import { FRAMEWORK_ISO27001 } from '../constants/frameworks/iso27001.js';
import { FRAMEWORK_ISO27002 } from '../constants/frameworks/iso27002.js';
import { FRAMEWORK_ISO42001 } from '../constants/frameworks/iso42001.js';
import { FRAMEWORK_ISO22301 } from '../constants/frameworks/iso22301.js';
import { FRAMEWORK_CIS18 } from '../constants/frameworks/cis18.js';
import { FRAMEWORK_GDPR } from '../constants/frameworks/gdpr.js';
import { FRAMEWORK_GDPR_ISAE3000 } from '../constants/frameworks/gdprIsae3000Processor.js';
import { FRAMEWORK_NIS2 } from '../constants/frameworks/nis2.js';
import { FRAMEWORK_DORA } from '../constants/frameworks/dora.js';
import { FRAMEWORK_PCI_DSS } from '../constants/frameworks/pciDss.js';
import { FRAMEWORK_NIST_CSF20 } from '../constants/frameworks/nistCsf20.js';
import { FRAMEWORK_CRA } from '../constants/frameworks/cra.js';
import { FRAMEWORK_EU_AI_ACT } from '../constants/frameworks/euAiAct.js';
import { FRAMEWORK_SKI_0215 } from '../constants/frameworks/ski-0215.js';

// Ordered list shown in the framework picker
export const GRC_FRAMEWORKS = [
  { id: 'fw-iso27001', label: 'ISO 27001:2022', framework: FRAMEWORK_ISO27001 },
  { id: 'fw-iso27002', label: 'ISO 27002:2022', framework: FRAMEWORK_ISO27002 },
  { id: 'fw-iso42001', label: 'ISO/IEC 42001:2023', framework: FRAMEWORK_ISO42001 },
  { id: 'fw-iso22301', label: 'ISO 22301:2019', framework: FRAMEWORK_ISO22301 },
  { id: 'fw-cis18', label: 'CIS Controls v8', framework: FRAMEWORK_CIS18 },
  { id: 'fw-gdpr', label: 'GDPR', framework: FRAMEWORK_GDPR },
  {
    id: 'fw-gdpr-isae3000',
    label: 'FSR ISAE 3000 — GDPR Processor',
    framework: FRAMEWORK_GDPR_ISAE3000,
  },
  { id: 'fw-nis2', label: 'NIS2', framework: FRAMEWORK_NIS2 },
  { id: 'fw-dora', label: 'DORA', framework: FRAMEWORK_DORA },
  { id: 'fw-pci-dss', label: 'PCI DSS v4', framework: FRAMEWORK_PCI_DSS },
  { id: 'fw-nist-csf-20', label: 'NIST CSF 2.0', framework: FRAMEWORK_NIST_CSF20 },
  { id: 'fw-cra', label: 'Cyber Resilience Act', framework: FRAMEWORK_CRA },
  { id: 'fw-eu-ai-act', label: 'EU AI Act', framework: FRAMEWORK_EU_AI_ACT },
  { id: 'fw-ski-0215', label: 'SKI 0215', framework: FRAMEWORK_SKI_0215 },
  { id: 'other', label: 'Other / Custom', framework: null },
];

/**
 * Returns a deduplicated, sorted list of controls for a given framework id.
 * Each control: { ref, externalId, section, title }
 * Returns [] for 'other' or unknown ids.
 */
export function getControlsForFramework(frameworkId) {
  const entry = GRC_FRAMEWORKS.find((f) => f.id === frameworkId);
  if (!entry || !entry.framework) return [];

  const seen = new Set();
  const controls = [];

  for (const m of entry.framework.mappings || []) {
    const key = m.externalId || m.ref;
    if (seen.has(key)) continue;
    seen.add(key);
    controls.push({
      ref: m.ref || m.externalId || '',
      externalId: m.externalId || m.ref || '',
      section: m.section || '',
      title: m.title || '',
    });
  }

  return controls;
}

/**
 * Returns the display label for a framework id.
 */
export function getFrameworkLabel(frameworkId) {
  return GRC_FRAMEWORKS.find((f) => f.id === frameworkId)?.label ?? frameworkId ?? '';
}
